import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { parsePostgresInterval } from '../utils/date';
import { useAuth } from '../contexts/auth/hooks';
import toast from 'react-hot-toast';

interface StaffMember {
  id: string;
  name: string;
  title?: string;
  profile_image_url?: string;
  settings: any; // << חשוב: כל האובייקט, לא רק rest_time
  price: number;
  duration: number;
  promotion?: any; // << המבצע של השירות
}

function parseSettings(raw: any) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
}
export function useAvailableStaff(serviceId: string, providedBusinessId?: string) {
  const { user, business } = useAuth();
  const [availableStaff, setAvailableStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  // הסר את ה־businessId מהסטייט, השתמש תמיד ב־providedBusinessId אם קיים
  const businessId = providedBusinessId || business?.id || null;

  useEffect(() => {
    // אין צורך ב־loadBusinessId יותר
    // ...remove useEffect for loadBusinessId...
  }, []); // אפשר למחוק את ה־useEffect הזה

  useEffect(() => {
    const loadAvailableStaff = async () => {
      if (!businessId || !serviceId) {

        setLoading(false);
        setAvailableStaff([]);
        return;
      }

      try {
        setLoading(true);
        
        // נביא את כל אנשי הצוות שמקושרים לשירות
        const { data: staffData, error: staffError } = await supabase
          .from('staff_services')
          .select(`
            staff_id,
            is_active,
            price,
            duration,
            users (
              id,
              name,
              title,
              profile_image_url,
              settings
            ),
            services (
              price,
              duration,
              promotion
            )
          `)
          .eq('service_id', serviceId)
          .eq('is_active', true);

        if (staffError) throw staffError;

        if (!staffData || staffData.length === 0) {
          // אם אין אנשי צוות, נביא את המנהל
          const { data: adminData, error: adminError } = await supabase
            .from('users')
            .select('*')
            .eq('business_id', businessId)
            .eq('role', 'admin')
            .single();

          if (adminError) throw adminError;

          // נביא את פרטי השירות
          const { data: serviceData, error: serviceError } = await supabase
            .from('services')
            .select('*')
            .eq('id', serviceId)
            .single();

          if (serviceError) throw serviceError;

    if (adminData) {
  setAvailableStaff([{
    id: adminData.id,
    name: adminData.name,
    title: adminData.title,
    profile_image_url: adminData.profile_image_url,
    settings: parseSettings(adminData.settings), // << פה גם כל ההגדרות
    price: serviceData.price,
    duration: parsePostgresInterval(serviceData.duration)
  }]);
} else {
  setAvailableStaff([]);
}
          return;
        }

        // Process staff data
   const staffMembers = staffData
  .filter(staffService => staffService.users)
  .map(staffService => {
    const staff = staffService.users;
    const service = staffService.services;

    if (!staff) return null;

    // אם יש price ב-staff_services, השתמש בו; אחרת השתמש במחיר ברירת המחדל של השירות
    const price = staffService.price ?? service?.price ?? 0;

    // אם יש duration ב-staff_services, השתמש בו; אחרת השתמש במשך ברירת המחדל של השירות
    const duration = staffService.duration || service?.duration || '30 minutes';
    const durationInMinutes = parsePostgresInterval(duration);

    const fullSettings = parseSettings(staff.settings);

    // שמור גם את ה-promotion של השירות
    const promotion = service?.promotion;

    return {
      id: staff.id,
      name: staff.name,
      title: staff.title,
      profile_image_url: staff.profile_image_url,
      settings: fullSettings,
      price,
      duration: durationInMinutes,
      promotion // הוסף את המבצע
    };
  })
  .filter((staff): staff is StaffMember => staff !== null);

        setAvailableStaff(staffMembers);
      } catch (error) {

        toast.error('שגיאה בטעינת אנשי הצוות הזמינים');
      } finally {
        setLoading(false);
      }
    };

    // וודא שה-serviceId הוא מחרוזת UUID תקינה
    if (typeof serviceId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(serviceId)) {
      loadAvailableStaff();
    } else {

      setLoading(false);
      setAvailableStaff([]);
    }
  }, [businessId, serviceId]);

  return { availableStaff, loading };
}